import { toast } from 'react-toastify';

import { blockedContactAction, contactWhoBlockedMeAction } from '../../../Actions/BlockAction';
import { GroupsMemberListAction } from '../../../Actions/GroupsAction';
import SDK from '../../../Components/SDK';
import { isAdminBlockedFlag } from '../../../Helpers/Chat/adminBlock';
import { formatToArrayofJid } from '../../../Helpers/Chat/BlockContact';
import { setGroupParticipantsByGroupId } from '../../../Helpers/Chat/Group';
import { formatUserIdToJid, getDataFromRoster, getIdFromJid, isSingleChatJID } from '../../../Helpers/Chat/User';
import logger from '../../../Helpers/logger';
import { i18n } from '../../../localization/i18n';
import { T, TOAST } from '../../../localization/toastKeys';

export const SEND_BLOCK_REASON = {
    BLOCKED_BY_ME: 'blockedByMe',
    BLOCKED_ME: 'blockedMe',
    ACCOUNT_UNAVAILABLE: 'accountUnavailable',
    NOT_A_MEMBER: 'notAMember',
};

/**
 * Why the user may not send to a chat right now (E36, FR103), from what the app already knows.
 * Broadcasts and chats whose state is not loaded yet are never blocked here.
 *
 * @param {object} state Redux state.
 * @param {string} jid Chat JID.
 * @param {object} roster Roster entry of the other user in a one-to-one chat.
 * @returns {string|null} A SEND_BLOCK_REASON, or null when sending is allowed.
 */
export const getSendBlockReason = (state, jid, roster = {}) => {
    if (!jid) return null;
    if (isSingleChatJID(jid)) {
        const userJid = formatUserIdToJid(getIdFromJid(jid));
        if ((state.blockedContact?.data || []).includes(userJid)) return SEND_BLOCK_REASON.BLOCKED_BY_ME;
        if ((state.contactsWhoBlockedMe?.data || []).includes(userJid)) return SEND_BLOCK_REASON.BLOCKED_ME;
        if (isAdminBlockedFlag(roster?.isAdminBlocked) || roster?.isDeletedUser === true) {
            return SEND_BLOCK_REASON.ACCOUNT_UNAVAILABLE;
        }
        return null;
    }
    const { groupJid, participants = [] } = state.groupsMemberListData?.data || {};
    if (getIdFromJid(groupJid) !== getIdFromJid(jid) || !participants.length) return null;
    const localUserId = state.vCardData?.data?.fromUser;
    const isMember = participants.some(participant => participant.userId === localUserId && participant.userType);
    return isMember ? null : SEND_BLOCK_REASON.NOT_A_MEMBER;
};

/**
 * Refuses a send the user is not allowed to make: shows E36 and reloads the chat window state.
 *
 * @param {string} jid Chat JID.
 * @returns {boolean} True when sending was refused.
 */
export const refuseIfSendBlocked = jid => (dispatch, getState) => {
    const roster = jid && isSingleChatJID(jid) ? getDataFromRoster(getIdFromJid(jid)) : {};
    if (!getSendBlockReason(getState(), jid, roster)) return false;
    dispatch(notifySendBlocked(jid));
    return true;
};

/**
 * Shows E36 and reloads the chat's block and membership state, so the composer switches to
 * its locked state.
 *
 * @param {string} jid Chat JID.
 */
export const notifySendBlocked = jid => dispatch => {
    toast.error(i18n.t(`${TOAST}.${T.CANNOT_SEND_IN_CONVERSATION}`));
    dispatch(reloadChatPermissions(jid)).catch(error => logger.error('Chat permissions not reloaded', { jid, error }));
};

const reloadChatPermissions = jid => async dispatch => {
    if (!jid) return;
    if (!isSingleChatJID(jid)) {
        const response = await SDK.getGroupParticipants(jid);
        if (response?.statusCode !== 200) return;
        setGroupParticipantsByGroupId(jid, response.data?.participants);
        dispatch(GroupsMemberListAction(response.data));
        return;
    }
    const [blockedByMe, blockedMe] = await Promise.all([SDK.getUsersIBlocked(), SDK.getUsersWhoBlockedMe()]);
    if (blockedByMe?.statusCode === 200) dispatch(blockedContactAction(formatToArrayofJid(blockedByMe.data)));
    if (blockedMe?.statusCode === 200) dispatch(contactWhoBlockedMeAction(formatToArrayofJid(blockedMe.data)));
    await SDK.getUserProfile(jid);
};
